<template>
  <v-card class="card-order-review-data">
    <v-card-title class="card-order-review-data__heading">{{ heading }}</v-card-title>

    <scrollable-container>
      <data-row>
        <v-flex>
          <one-data-column>
            <data-label slot="label">User ID</data-label>
            <div class="t-user-id">{{ cardOrder.userId }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Client</data-label>
            <div class="t-review-client font-weight-medium">{{ cardOrderProfile.name }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Card type</data-label>
            <div class="t-review-type">{{ cardOrder.cardType | cardType }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Card account</data-label>
            <div class="t-review-account">{{ cardOrder.accountId || 'New account' }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Card currency</data-label>
            <div class="t-review-currency">{{ cardOrder.currency }}</div>
          </one-data-column>

          <one-data-column :label-props="{ style: 'display: none;' }">
            <data-divider />
          </one-data-column>

          <one-data-column>
            <data-label
              slot="label"
              highlight
            >Card holder</data-label>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Type</data-label>
            <div class="t-review-holder-type">{{ orderHolderTypeName }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Name on card</data-label>
            <div class="t-review-holder">{{ cardOrder.holderName }}</div>
          </one-data-column>

          <template v-if="cardOrder.isForLegal">
            <one-data-column>
              <data-label slot="label">Company name</data-label>
              <div class="t-create-company">{{ cardOrder.companyName }}</div>
            </one-data-column>

            <one-data-column v-if="cardOrder.isRealCardType">
              <data-label slot="label">Company name on card</data-label>
              <div class="t-create-name-on-card">{{ cardOrder.nameOnCard | nameOnCard }}</div>
            </one-data-column>
          </template>

          <one-data-column>
            <data-label slot="label">Phone number</data-label>
            <div class="t-review-holder-phone">{{ cardOrder.holderMobilePhone }}</div>
          </one-data-column>

          <one-data-column :label-props="{ style: 'display: none;' }">
            <data-divider />
          </one-data-column>

          <template v-if="cardOrder.isRealCardType">
            <one-data-column>
              <data-label
                slot="label"
                highlight
              >Delivery address</data-label>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Recipient</data-label>
              <div class="t-review-recipient">{{ cardOrder.recipientName }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Phone number</data-label>
              <div class="t-review-recipient-phone">{{ cardOrder.recipientMobilePhone }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Country</data-label>
              <div class="t-review-country">{{ getCountryName(cardOrder.addressCountry) }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">City</data-label>
              <div class="t-review-city">{{ cardOrder.addressCity }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Street</data-label>
              <div class="t-review-street">{{ cardOrder.addressStreet }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Postcode</data-label>
              <div class="t-review-postcode">{{ cardOrder.addressZipCode }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Free delivery</data-label>
              <div class="t-review-free-delivery">{{ cardOrder.disableDeliveryFee | booleanValue }}</div>
            </one-data-column>

            <one-data-column>
              <data-label slot="label">Delivery type</data-label>
              <div class="t-review-delivery">{{ cardOrder.shipmentType }}</div>
            </one-data-column>

            <one-data-column :label-props="{ style: 'display: none;' }">
              <data-divider />
            </one-data-column>
          </template>

          <one-data-column>
            <data-label
              slot="label"
              highlight
            >Billing address</data-label>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Country</data-label>
            <div class="t-review-avs-country">{{ getCountryName(cardOrder.avsAddressCountry) }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">City</data-label>
            <div class="t-review-avs-city">{{ cardOrder.avsAddressCity }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Street</data-label>
            <div class="t-review-avs-street">{{ cardOrder.avsAddressStreet }}</div>
          </one-data-column>

          <one-data-column>
            <data-label slot="label">Postcode</data-label>
            <div class="t-review-avs-postcode">{{ cardOrder.avsAddressZipCode }}</div>
          </one-data-column>

          <slot />
        </v-flex>
      </data-row>
    </scrollable-container>

    <v-divider class="grey lighten-3" />
    <v-card-actions class="card-order-review-data__actions">
      <slot name="actions" />
    </v-card-actions>
  </v-card>
</template>

<script>
  // Models
  import CardOrder from '@/models/CardOrder'
  import AccountProfile from '@/models/AccountProfile'

  // Utils
  import { getCardHolderTypeLabel } from '@/utils/CardOrders'

  // Mixins
  import countries from '@/mixins/countries'

  // Components
  import ScrollableContainer from '@/components/ui/ScrollableContainer'
  import DataRow from '@/components/ui/Data/DataRow'
  import OneDataColumn from '@/components/ui/Data/OneDataColumn'
  import DataLabel from '@/components/ui/Data/DataLabel'
  import DataDivider from '@/components/ui/Data/DataDivider'

  export default {
    name: 'CardOrderReviewData',
    components: {
      ScrollableContainer,
      DataRow,
      OneDataColumn,
      DataLabel,
      DataDivider
    },
    mixins: [countries],
    props: {
      heading: {
        required: true,
        type: String
      },
      cardOrder: {
        required: true,
        type: CardOrder
      },
      cardOrderProfile: {
        required: true,
        type: AccountProfile
      }
    },
    computed: {
      orderHolderTypeName () {
        return getCardHolderTypeLabel(this.cardOrderProfile.profileType, this.cardOrder.holderType)
      }
    }
  }
</script>

<style lang="scss">
  .card-order-review-data {
    font-size: 16px;
    font-weight: 400;

    &__heading,
    .container {
      padding-right: 30px;
      padding-left: 30px;
    }

    &__heading {
      font-size: 24px;
      font-weight: 400;
      line-height: 32px;
      padding-top: 21px;
    }

    &__actions {
      padding: 14px 14px;
    }
  }
</style>
